import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

/**
 * Master-admin private pages (Evan 2026-11-02: "i should be able to make any page visible to only
 * me for my own use"). Keys are route paths ('/growth') or tabs inside a page ('/growth#team'),
 * stored in public.admin_private_pages — every admin can read the list (so their sidebar can hide
 * those pages), only the master admin (is_master_admin()) can change it. RLS enforces that; this
 * file just draws the UI from it.
 */

type Ctx = {
  isMaster: boolean;
  ready: boolean;
  isPrivate: (key: string) => boolean;
  /** true when `key` is private AND the viewer isn't the master admin */
  isHidden: (key: string) => boolean;
  toggle: (key: string) => Promise<void>;
};

const PrivatePagesContext = createContext<Ctx>({
  isMaster: false,
  ready: false,
  isPrivate: () => false,
  isHidden: () => false,
  toggle: async () => {},
});

export function PrivatePagesProvider({ children }: { children: React.ReactNode }) {
  const [isMaster, setIsMaster] = useState(false);
  const [keys, setKeys] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    const [m, k] = await Promise.all([
      supabase.rpc('is_master_admin'),
      supabase.from('admin_private_pages').select('key'),
    ]);
    setIsMaster(m.data === true);
    setKeys(new Set(((k.data ?? []) as { key: string }[]).map((r) => r.key)));
    setReady(true);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback(
    async (key: string) => {
      if (!isMaster) return;
      const { error } = keys.has(key)
        ? await supabase.from('admin_private_pages').delete().eq('key', key)
        : await supabase.from('admin_private_pages').insert({ key });
      if (error) window.alert(error.message);
      await load();
    },
    [isMaster, keys, load],
  );

  const value = useMemo<Ctx>(
    () => ({
      isMaster,
      ready,
      isPrivate: (key) => keys.has(key),
      isHidden: (key) => !isMaster && keys.has(key),
      toggle,
    }),
    [isMaster, ready, keys, toggle],
  );

  return <PrivatePagesContext.Provider value={value}>{children}</PrivatePagesContext.Provider>;
}

export function usePrivatePages() {
  return useContext(PrivatePagesContext);
}

/** The private-page key for a pathname: its first segment ('/growth/x' → '/growth', '/' → '/'). */
export function pageKeyFor(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg ? `/${seg}` : '/';
}

/** Master-only "Only me" switch. Renders nothing for anyone else. */
export function OnlyMeToggle({ pageKey, className }: { pageKey: string; className?: string }) {
  const { isMaster, isPrivate, toggle } = usePrivatePages();
  const [busy, setBusy] = useState(false);
  if (!isMaster) return null;
  const on = isPrivate(pageKey);
  return (
    <button
      type="button"
      disabled={busy}
      title={on ? 'Only you can see this. Click to show it to all admins.' : 'Visible to all admins. Click to make it only yours.'}
      onClick={async () => {
        setBusy(true);
        await toggle(pageKey);
        setBusy(false);
      }}
      className={
        className ??
        `inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] disabled:opacity-50 ${
          on ? 'border-accent/60 bg-accent/15 text-accent' : 'border-borderStrong text-mutedDim hover:text-textPrimary'
        }`
      }
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        {on ? (
          <path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 0 1 6 0v3H9Z" />
        ) : (
          <path d="M17 10V7a5 5 0 0 0-9.9-1 1 1 0 1 0 1.96.4A3 3 0 0 1 15 7v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1Z" />
        )}
      </svg>
      {on ? 'Only me' : 'All admins'}
    </button>
  );
}
